import React, { useMemo, useEffect, useState, useContext } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useLocation } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { useNavigate } from 'react-router-dom';

import TopBanner from "@/app/pages/companyProfile/topBanner/TopBanner";
import Overview from "@/app/pages/companyProfile/overview/Overview";
import CompanyAddedBy from "@/app/pages/companyProfile/addedBy/CompanyAddedBy";
import Contacts from "@/app/pages/companyProfile/contact/Contacts";
import Anchors from "@/app/pages/companyProfile/anchor/Anchor";
import HooversAdBlock from "@/app/pages/companyProfile/hooversAdBlock";
import ScoreRatingsAdPurple from "@/app/pages/companyProfile/scoreRatingsAdPurple";
import CreditReports from "@/app/pages/companyProfile/creditReports/CreditReports";
import CorporateFamily from "@/app/pages/companyProfile/corporateFamily/CorporateFamily";
import Financial from "@/app/pages/companyProfile/financial/Financials";
import TopCompetitors from "@/app/pages/companyProfile/topCompetitors";
import InTheAreasOf from "@/app/pages/companyProfile/inTheAreasOf/inTheAreasOf";
import FAQsComponent from "@/app/pages/companyProfile/businessDirectoryAccordion";
import CompanyLinkage from "@/app/pages/companyProfile/companyLinkage/CompanyLinkage";
import { getCompanyProfileData } from "@/services/businessDirectory";
import Loader from "@/components/loader";
import Overlay from "@/components/overlay";

import { areAllValuesNull, countryDictionary } from "@/utils";
import { isNotUndefinedOrNull, isNotUndefined, dnbComLog, LogSeverity } from "@/shared/FunctionCore";
import { setDigitalData } from "@/services/setDigitalData";
import { AppContext, ContextType } from "@/shared/context";
import SkyscraperAdComponent from "@/components/ads/SkyscraperAdComponent";
import { companyProfileDesktopOne, companyProfileDesktopThree, companyProfileDesktopTwo, companyProfileDesktopSix, companyProfileGoogleAdsData } from "@/components/ads/GoogleAdsData";
import BusinessDirectoryAdBannerComponent from "@/app/pages/companyProfile/businessDirectoryAdBanner";
import { adBannerProperties } from "@/app/pages/companyProfile/businessDirectoryAdBanner/businessDirectoryAdBannerData";
import SimilarCompany from "@/app/pages/companyProfile/similarCompanies/similarCompany";
import MarketPlaceAds from "@/components/ads/MarketPlaceAds";
import SingleAds from "@/components/ads/SingleAds";
import { BusinessDirectoryOptoutComponent } from "@/app/pages/companyProfile/BusinessDirectoryOptout/BusinessDirectoryOptout";
import { businessDirectoryOptoutData } from "@/app/pages/companyProfile/BusinessDirectoryOptout/BusinessDirectoryOptoutData";

const CompanyProfile = ({ countryCode, languageCode }): JSX.Element => {
  const { t } = useTranslation();
  const { isUnitedStates, isEuBd } = useContext(AppContext) as ContextType
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [companyData, setCompanyData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  setDigitalData({gcpPageName: 'companyProfile', companyName: ''});

  const fetchData = () => {
    setIsLoading(true);
    try {
      let path = pathname.replace(".html", "");
      
      const splittedPath = path.split("/")
      if (isNotUndefined(splittedPath[3])) {
        const encodedQS = encodeURI(splittedPath[3])
        splittedPath[3] = encodedQS
        path = splittedPath.join("/")
      }

      getCompanyProfileData(path, languageCode, countryCode)
        .then((res) => {
          if (res && res.data !== null) {
            dnbComLog(LogSeverity.INFO, res.data);
            const companyProfileResponseBooleanResult = areAllValuesNull(res.data);
            if (companyProfileResponseBooleanResult) {
              navigate(t("bdLandingPageRedirectionLink"));
              return;
            }
            setCompanyData(res.data);
            setDigitalData({gcpPageName: 'companyProfile', companyName: res?.data?.header?.companyName});
            setIsLoading(false);
          }
        })
        .catch((err) => {
          setIsLoading(false);
          dnbComLog(LogSeverity.ERROR, err.message);
          if(err.response.status === 404) {
            navigate(t("bdLandingPageRedirectionLink"));
          }
        });
    } catch (error) {
      dnbComLog(LogSeverity.ERROR, error);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (pathname) {
      fetchData();
    }
    // else {
    // Display no data found page // Coming soon....
    // }
  }, [pathname]);

  let browserTitle = "Dun & Bradstreet";

  browserTitle = useMemo(() => {
    if (companyData !== null) {
      const name = companyData['header'].companyProfileHeaderTitle;
      const country = countryDictionary[countryCode]
      const city = companyData['header'].companyNewHeaderParameter.companyInformationForCookie.companyCity;
      const state = companyData['header'].companyNewHeaderParameter.companyInformationForCookie.companyState;
      
      const address = [city, state, country].filter(word => isNotUndefinedOrNull(word)).join(", ")

      return `${name} ${t("companyProfile.companyProfileText")} | ${address} |
        ${t("companyProfile.browserTitleThirdWord")} - Dun & Bradstreet`
    }

    return "Dun & Bradstreet"
  }, [companyData]);


  return (
    <>
      <Helmet>
        <title>{browserTitle}</title>
      </Helmet>
      <div id="page">
        <ToastContainer position="top-center" />
        {isLoading ? (
          <Loader inProgress={isLoading} />
        ) : companyData && (<>
          <div className="main_content">
            <div id="company_new_header">
              <div className="company-profile-header-bg-img">
               <TopBanner props={companyData['header']} breadcrumb={companyData['breadcrumb']} />
              </div>
            </div>
            {isUnitedStates && <SkyscraperAdComponent googleAdsData={companyProfileGoogleAdsData} />}
            <Anchors props={companyData['header']} />
             <div className="mt-4">
              {isUnitedStates && (
                <BusinessDirectoryAdBannerComponent  properties={adBannerProperties} companyHeaderData={companyData['header']} countryCode= {countryCode}  />
              )}
              <Overview props={companyData['overview']} countryCode={countryCode} />
              {isUnitedStates && <MarketPlaceAds page="CompanyProfile" />}
              {isUnitedStates && companyData['companyEnhancedFields'] && companyData?.companyEnhancedFields?.companyEnhancedFieldsNullCheck &&<CompanyAddedBy companyEnhancedFields={companyData?.companyEnhancedFields} contacts={companyData?.contacts}  />}
              {companyData['contacts'] && <Contacts props={companyData['contacts']} />}
              {isEuBd && <HooversAdBlock forPage="companyProfile" />}
              {isUnitedStates && <HooversAdBlock forPage="companyProfile" />}
              {isUnitedStates && <SingleAds AdName={companyProfileDesktopOne} />}
              {companyData['financials'] && <Financial props={companyData['financials']} />}
              {isUnitedStates && <ScoreRatingsAdPurple forPage="companyProfilePurpleAd" />}
              {isUnitedStates && <SingleAds AdName={companyProfileDesktopTwo} />}
              {isUnitedStates && companyData['competitors'] && <TopCompetitors props={companyData['competitors']} />}
              {isUnitedStates && <SingleAds AdName={companyProfileDesktopThree} />}
              {isEuBd && <ScoreRatingsAdPurple forPage="companyProfilePurpleAd" />}
              {isEuBd && <CorporateFamily />}
              {isUnitedStates && <SimilarCompany />} 
              {isUnitedStates && <CorporateFamily />} 
              {<CreditReports countryCode={countryCode} />}
              {isEuBd && <ScoreRatingsAdPurple forPage="companyProfilePurpleAd" /> }
              {isEuBd && <InTheAreasOf /> }
              {isUnitedStates && companyData["faq"] && (
                <FAQsComponent faqsData={companyData["faq"]} />
              )}
              {isUnitedStates && companyData['companyLinkage'] && <CompanyLinkage props={companyData['companyLinkage']} />}
              {isUnitedStates && <BusinessDirectoryOptoutComponent companyName={companyData['header']?.companyProfileHeaderTitle} {...businessDirectoryOptoutData}  />}
              {isUnitedStates && <SingleAds AdName={companyProfileDesktopSix} />}
            </div>
          </div>
          <Overlay
            countryCode={countryCode}
            isEmailDisplay={['at','ch','hu','pl','us'].includes(countryCode) }
            isPhoneDisplay={['de','at','ch','hu','us'].includes(countryCode)}
            isRecaptchaDisplay={['de','at','ch','hu','pl','us'].includes(countryCode)}
            recaptchaActionName={t("companyProfile.recaptchActionName")}
          />
        </>
        )}
      </div>
    </>
  );
};

export default CompanyProfile;
